import Slash from '@griply/assets/src/icons/interface/slash.svg?react';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, keyframes } from 'styled-components';

const loadingAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: ${({ theme }) =>
    `${theme.spacing(1.5)} ${theme.spacing(5)} ${theme.spacing(1)} ${theme.spacing(4)}`};
`;

export const Body = styled.div<{ $isFrozen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;

  color: ${({ theme }) => theme.palette.text.primary};

  ${({ $isFrozen }) =>
    $isFrozen &&
    css`
      opacity: 0.5;
    `};
`;

export const CurrentProgress = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-width: 0;

  color: ${({ theme }) => theme.palette.text.primary};
`;

export const CurrentProgressValue = styled.span<{
  $isEditable?: boolean;
  $isEmpty?: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.h4)};
  color: ${({ theme, $isEmpty }) =>
    $isEmpty ? theme.palette.text.secondary : theme.palette.text.primary};
  padding: 0 ${({ theme }) => theme.spacing(0.75)};

  ${({ $isEditable, theme }) =>
    $isEditable &&
    css`
      background: none;
      border: none;
      border-radius: ${theme.spacing(1.6)};
      cursor: pointer;

      &:hover,
      &:focus-visible {
        color: ${({ theme }) =>
          theme.palette.primaryExtended.opacityBackgroundText};
        background: ${({ theme }) =>
          theme.palette.primaryExtended.opacityBackgroundBackground};
      }
    `};
`;

export const CurrentProgressTarget = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(1)};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const CurrentProgressSlash = styled(Slash)`
  width: auto;
  height: 12px;
  margin-right: ${({ theme }) => theme.spacing(1)};

  color: ${({ theme }) => theme.palette.grey[600]};
`;

export const NoTasksLabel = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.h4)};
  padding: 0 ${({ theme }) => theme.spacing(1)};

  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const IconButtonContainer = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.palette.grey['400']};
`;

export const Progress = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;
  flex-shrink: 0;

  color: ${({ theme }) => theme.palette.text.primary};
`;

export const Difference = styled.span<{ $positive?: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.caption)};
  display: flex;
  align-items: center;

  color: ${({ $positive, theme }) =>
    $positive ? theme.palette.success.dark : theme.palette.error.dark};
`;

export const DifferenceIconContainer = styled.span<{ $up: boolean }>`
  font-size: 1.2rem;
  line-height: 62.5%;

  transform: rotate(${({ $up }) => ($up ? 0 : 180)}deg);
`;

export const Footer = styled.div<{ $isFrozen: boolean }>`
  display: flex;
  justify-content: flex-end;
  flex: 1 1 0;
  min-height: 118px;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(1.5)};

  ${({ $isFrozen }) =>
    $isFrozen &&
    css`
      opacity: 0.5;
    `};
`;

export const LoadingBody = styled.div`
  width: 100%;
  height: 5.2rem;
  padding: ${({ theme }) =>
    `${theme.spacing(0.5)} ${theme.spacing(4)} ${theme.spacing(1)}`};

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;

    background: ${({ theme }) =>
      `linear-gradient(90deg, ${theme.palette.grey['100']} 25%, ${theme.palette.grey['50']} 50%, ${theme.palette.grey['100']} 75%)`};
    background-size: 200% 100%;
    border-radius: ${({ theme }) => theme.spacing(1)};
    animation: ${loadingAnimation} 1.5s infinite linear;
  }
`;

export const LoadingFooter = styled.div`
  width: 100%;
  height: 12rem;
  padding: ${({ theme }) =>
    `${theme.spacing(1)} ${theme.spacing(4)} ${theme.spacing(4)}`};

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;

    background: ${({ theme }) =>
      `linear-gradient(90deg, ${theme.palette.grey['100']} 25%, ${theme.palette.grey['50']} 50%, ${theme.palette.grey['100']} 75%)`};
    background-size: 200% 100%;
    border-radius: ${({ theme }) => theme.spacing(1)};
    animation: ${loadingAnimation} 1.5s infinite linear;
  }
`;
